import React, { useState } from 'react';
import { categories } from '../config/apps';
import { sendSolutionRequestEmail } from '../services/emailService';
import './SuggestionModal.css';

const SuggestionModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    department: '',
    customDepartment: '',
    title: '',
    description: '',
    contactName: '',
    contactEmail: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const departmentOptions = [
    ...categories.map(cat => ({ 
      id: cat.id, 
      name: cat.name, 
      icon: cat.icon 
    })),
    { id: 'finance', name: 'Finance & Accounting', icon: '💰' },
    { id: 'marketing', name: 'Marketing & Communications', icon: '📢' },
    { id: 'legal', name: 'Legal & Compliance', icon: '⚖️' },
    { id: 'executive', name: 'Executive & Management', icon: '👔' },
    { id: 'facilities', name: 'Facilities & Operations', icon: '🏢' },
    { id: 'other', name: 'Other (Please specify)', icon: '📝' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.department || !formData.title || !formData.description || !formData.contactName || !formData.contactEmail) {
      alert('Please fill in all required fields.');
      return;
    }

    if (formData.department === 'other' && !formData.customDepartment) {
      alert('Please specify your department.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const emailData = {
        department: formData.department,
        customDepartment: formData.customDepartment,
        title: formData.title,
        description: formData.description,
        contactName: formData.contactName,
        contactEmail: formData.contactEmail,
        type: 'suggestion'
      };

      await sendSolutionRequestEmail(emailData);
      
      setSubmitStatus('success');
      
      setTimeout(() => {
        setFormData({
          department: '',
          customDepartment: '',
          title: '',
          description: '',
          contactName: '',
          contactEmail: ''
        });
        onClose();
        setSubmitStatus(null);
      }, 2000);

    } catch (error) {
      console.error('Error sending suggestion:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="suggestion-modal-overlay" onClick={onClose}>
      <div className="suggestion-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="suggestion-modal-header">
          <h2>Make a Suggestion</h2>
          <button 
            className="suggestion-modal-close" 
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {submitStatus === 'success' && (
          <div className="submit-success">
            <div className="success-icon">✅</div>
            <h3>Suggestion Submitted Successfully!</h3>
            <p>Thank you for your suggestion! Our team will review it and prioritize based on business impact and feasibility. We'll contact you if we need more information.</p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="submit-error">
            <div className="error-icon">❌</div>
            <h3>Submission Failed</h3>
            <p>There was an error sending your suggestion. Please try again or contact IT support.</p>
          </div>
        )}

        {!submitStatus && (
          <form onSubmit={handleSubmit} className="suggestion-form">
            <div className="form-notice">
              <span className="notice-icon">ℹ️</span>
              <p>All suggestions are welcome! We review each submission and prioritize based on business value, feasibility, and resource availability.</p>
            </div>

            <div className="form-section">
              <h3>📋 Suggestion Details</h3>
              
              <div className="form-group">
                <label htmlFor="department">Department *</label>
                <select
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  required
                  className="form-select"
                >
                  <option value="">Select your department...</option>
                  {departmentOptions.map(dept => (
                    <option key={dept.id} value={dept.id}>
                      {dept.icon} {dept.name}
                    </option>
                  ))}
                </select>
              </div>

              {formData.department === 'other' && (
                <div className="form-group">
                  <label htmlFor="customDepartment">Please specify your department *</label>
                  <input
                    type="text"
                    id="customDepartment"
                    name="customDepartment"
                    value={formData.customDepartment}
                    onChange={handleInputChange}
                    placeholder="e.g., Research & Development"
                    required
                    className="form-input"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder=""
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Detailed Description *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your suggestion..."
                  required
                  className="form-textarea"
                  rows="6"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>👤 Contact Information</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="contactName">Your Name *</label>
                  <input
                    type="text"
                    id="contactName"
                    name="contactName"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    placeholder="Full name"
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="contactEmail">Your Email *</label>
                  <input
                    type="email"
                    id="contactEmail"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleInputChange}
                    placeholder="your.email@knipper.com"
                    required
                    className="form-input"
                  />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="button" 
                onClick={onClose}
                className="btn-cancel"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="loading-spinner"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit Suggestion
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SuggestionModal;
